import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { IchraProductClassWizardNavItemsMapping } from 'adminDashboard/benefitDetails/design/ichra/finishBenefitDesign/productClassWizardModal/ProductClassStepMapping'

import {
  BenefitProductClassResponse,
  ProductClassDesignSummaryResponse,
  ProductClassesInfo,
  getBenefitProductClasses,
  getIchraProductClassDesignWizard,
  getIchraProductClassDesignWizardState,
  getProductClassDesignSummary,
} from 'core'
import { ProductClassStatus } from 'core/domain/benefit/productClassStatus'

import ConfirmModal from 'common/components/dataDisplay/ConfirmModal'

import { Button, Col, Collapse, CollapseItem, Dropdown, Row, Skeleton, Switch, Tag } from 'common/components'
import { formatYear, parseZoneDayMonthYearFormat } from 'common/dateFormatters'
import { useQueryRequestV2 } from 'common/hooks'

import { ExtraHeaderContent } from '../ProductClassSummaryComponents'
import ProductClassWizardModal from '../productClassWizardModal'
import menu from './ActionMenu'
import SubCollapse from './SubCollapse'

import './product-class-details.less'

type ProductClassDetailsV3Props = {
  item: BenefitProductClassResponse
  collapseKey: number
  benefitStartDate: string
  benefitEndDate: string
  colSpan?: number
  showIcon?: boolean
  showBenefitDates?: boolean
  isRenewal?: boolean
  productClassesInfo: ProductClassesInfo
  planPeriodId?: string
  onEdit?: (productClass: BenefitProductClassResponse) => void
  onDesignClass?: (productClass: BenefitProductClassResponse) => void
  onArchive?: (productClass: BenefitProductClassResponse) => void
  onDelete?: (productClass: BenefitProductClassResponse) => void
  onRenewalEdit?: (productClass: BenefitProductClassResponse) => void
  onRenewalDelete?: (productClass: BenefitProductClassResponse) => void
  onSubmit?: () => Promise<void>
}

const ProductClassDetailsV3 = ({
  item,
  collapseKey,
  benefitStartDate,
  benefitEndDate,
  colSpan = 14,
  showIcon,
  showBenefitDates,
  isRenewal,
  productClassesInfo,
  planPeriodId,
  onEdit,
  onDesignClass,
  onArchive,
  onDelete,
  onRenewalDelete,
  onRenewalEdit,
  onSubmit,
}: ProductClassDetailsV3Props) => {
  const { t: translate } = useTranslation()
  const { data: productClassDetails, isLoading: isProductClassDetailsLoading } =
    useQueryRequestV2<ProductClassDesignSummaryResponse>({
      queryFn: () => getProductClassDesignSummary(item.id),
    })

  const [offer, setOffer] = useState(false)

  const handleOfferChange = async (checked: boolean) => {
    console.log('handleOfferChange')
  }

  const [isDeleteClassModalOpen, setDeleteClassModalOpen] = useState(false)
  const [selectedDeleteProductClassId, setDeleteProductClassId] = useState('')

  const handleDeleteModal = (productClassId: string) => {
    setDeleteClassModalOpen(true)
    setDeleteProductClassId(productClassId)
  }

  const [isEditClassModalOpen, setEditClassModalOpen] = useState(false)
  const [selectedEditProductClassId, setEditProductClassId] = useState('')

  const handleEditModal = (productClassId: string) => {
    setEditClassModalOpen(true)
    setEditProductClassId(productClassId)
  }

  const {
    data: productClasses,
    sendQuery: sendGetProductClasses,
    isLoading: isProductClassesLoading,
    reload: reloadProductClasses,
  } = useQueryRequestV2<BenefitProductClassResponse[]>({
    initialState: null,
    queryFn: () => getBenefitProductClasses(item.id),
    enable: !!item.id,
  })

  const onProductClassWizardModalClosed = async () => {
    reloadProductClasses(), setEditClassModalOpen(false)
  }

  return (
    <>
      <Row className="mb-8">
        <Col sm={24}>
          <Collapse defaultActiveKey={[collapseKey]}>
            <CollapseItem
              key={collapseKey}
              header={
                <div className="flex ">
                  {!isRenewal && item.status === ProductClassStatus.Active && (
                    <Tag color="green">
                      <i className="far fa-check-circle color-green-6 mr-4" />
                      Active
                    </Tag>
                  )}
                  {!isRenewal && item.status === ProductClassStatus.Draft && (
                    <Tag>
                      <i className="far fa-info-circle color-gray-8 mr-4" />
                      Draft
                    </Tag>
                  )}
                  <span className="class_name">{item.name}</span>
                </div>
              }
              extra={
                <ExtraHeaderContent
                  content={
                    isRenewal ? (
                      <div className="flex">
                        <div className="mr-8">Offering for {formatYear(benefitStartDate)}</div>
                        <div>
                          <Switch checked={offer} onChange={handleOfferChange} />
                        </div>
                      </div>
                    ) : item.status === ProductClassStatus.Draft ? (
                      <Row>
                        <Col span={24}>
                          <Button className="mr-16" onClick={() => handleEditModal(item.id)}>
                            {translate('admin-dashboard.benefit-details.summary.edit-class-button')}
                          </Button>
                          <Button danger className="mr-16" onClick={() => handleDeleteModal(item.id)}>
                            {translate('admin-dashboard.benefit-details.summary.delete-class-v2')}
                          </Button>
                        </Col>
                      </Row>
                    ) : (
                      <Dropdown overlay={menu(item.status, item.id, handleEditModal, handleDeleteModal)}>
                        <Button>
                          Actions <i className="far fa-angle-down ml-4"></i>
                        </Button>
                      </Dropdown>
                    )
                  }
                />
              }
              content={
                <Skeleton loading={isProductClassDetailsLoading}>
                  {productClassDetails && (
                    <SubCollapse
                      productClassDetails={productClassDetails}
                      benefitStartDate={benefitStartDate}
                      benefitEndDate={benefitEndDate}
                    />
                  )}
                  {!isRenewal ? (
                    <>
                      <ConfirmModal
                        title="Delete ICHRA class"
                        visible={isDeleteClassModalOpen}
                        buttonText="Confirm"
                        children={
                          <div>
                            <p>Do you want to delete the draft {item.name} class?</p>
                          </div>
                        }
                        onConfirm={() => {
                          console.log('confirm')
                          setDeleteClassModalOpen(false)
                        }}
                        onCancel={() => {
                          setDeleteClassModalOpen(false)
                        }}
                      ></ConfirmModal>
                      <ProductClassWizardModal
                        navigationMapping={IchraProductClassWizardNavItemsMapping}
                        getProductClassWizard={() => {
                          return getIchraProductClassDesignWizard(item.id)
                        }}
                        getProductClassWizardState={() => {
                          return getIchraProductClassDesignWizardState(item.id)
                        }}
                        visible={isEditClassModalOpen}
                        id={selectedEditProductClassId}
                        planPeriodId={planPeriodId}
                        afterClose={async () => {
                          await onProductClassWizardModalClosed()
                        }}
                        onSubmit={async () => {
                          await reloadProductClasses()
                        }}
                        wizardTitle="Benefit design"
                      />
                    </>
                  ) : null}
                </Skeleton>
              }
            />
          </Collapse>
        </Col>
      </Row>
    </>
  )
}

export default ProductClassDetailsV3
